/**
 * MapPage — Full-featured location map v3
 * Geofences: circle + polygon drawing
 * Alarms: visual (flash overlay) + audio (Web Audio API)
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  MapContainer, TileLayer, Marker, Popup, useMap,
  Polyline, Circle, Polygon, useMapEvents,
} from 'react-leaflet';
import L from 'leaflet';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import useStore from '../store/useStore';
import { getSocket } from '../hooks/useSocket';
import usePTT from '../hooks/usePTT';
import {
  ArrowLeft, RefreshCw, MapPin, AlertTriangle,
  History, Download, X, Layers, Navigation,
  Users, Volume2, Mic, MicOff, Radio,
} from 'lucide-react';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// 30 minutes of GPS history — visible as fading polyline (estela). Seeded from
// /api/map/history-recent/all on load, then extended live via user:locationUpdate.
const TRAIL_MS = 30 * 60 * 1000;
const TRAIL_MAX_POINTS = 400;
const STOP_THRESHOLD_MS = 3 * 60 * 1000;

// ── Audio Engine ──────────────────────────────────────────────
function playAlarmSound(type = 'exit') {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const sequences = {
      exit:  [{ f:880, d:0.15 }, { f:660, d:0.15 }, { f:440, d:0.3 }],
      enter: [{ f:440, d:0.1 }, { f:660, d:0.1 }, { f:880, d:0.2 }],
      speed: [{ f:1200, d:0.1 }, { f:1200, d:0.1 }, { f:1200, d:0.3 }],
      panic: [{ f:800, d:0.2 }, { f:0, d:0.05 }, { f:800, d:0.2 }, { f:0, d:0.05 }, { f:800, d:0.4 }],
    };
    const notes = sequences[type] || sequences.exit;
    let t = ctx.currentTime;
    notes.forEach(({ f, d }) => {
      if (f === 0) { t += d; return; }
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.type = 'square';
      osc.frequency.setValueAtTime(f, t);
      gain.gain.setValueAtTime(0.18, t);
      gain.gain.exponentialRampToValueAtTime(0.001, t + d);
      osc.start(t); osc.stop(t + d);
      t += d;
    });
  } catch (_) {}
}

// ── Visual Alarm Overlay ───────────────────────────────────────
function AlarmOverlay({ alarms, onDismiss, onLocate }) {
  if (!alarms.length) return null;
  const a = alarms[0];
  const isExit = a.type === 'exit';
  const isSpeed = a.alertType === 'speed';
  const color = isSpeed ? '#FF9500' : isExit ? '#FF3B30' : '#34C759';

  return (
    <>
      {/* Full-screen edge flash */}
      <div style={{
        position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 9998,
        boxShadow: `inset 0 0 80px 20px ${color}55`,
        animation: 'alarmFlash 0.5s ease-in-out 3',
      }} />
      <style>{`@keyframes alarmFlash{0%,100%{opacity:0}50%{opacity:1}}`}</style>

      {/* Notification card — glass */}
      <div style={{
        position: 'fixed', top: 70, left: '50%', transform: 'translateX(-50%)',
        zIndex: 9999,
        background: 'rgba(255,255,255,0.92)',
        WebkitBackdropFilter: 'saturate(180%) blur(30px)',
        backdropFilter: 'saturate(180%) blur(30px)',
        border: '1px solid rgba(255,255,255,0.7)',
        borderLeft: `3px solid ${color}`,
        borderRadius: 14, padding: '14px 20px', minWidth: 320, maxWidth: 480,
        display: 'flex', alignItems: 'flex-start', gap: 12,
        boxShadow: `0 20px 50px rgba(0,0,0,.14), 0 4px 12px ${color}33`,
        animation: 'slideDown 0.3s ease-out',
      }}>
        <style>{`@keyframes slideDown{from{transform:translateX(-50%) translateY(-20px);opacity:0}to{transform:translateX(-50%) translateY(0);opacity:1}}`}</style>
        <div style={{ fontSize: 28, lineHeight: 1 }}>
          {isSpeed ? '⚡' : isExit ? '🚨' : '📍'}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 800, fontSize: 13, color, marginBottom: 3, letterSpacing:'0.4px' }}>
            {isSpeed ? 'EXCESO DE VELOCIDAD' : isExit ? 'SALIDA DE GEOCERCA' : 'ENTRADA A GEOCERCA'}
          </div>
          <div style={{ fontSize: 13, color: '#1D1D1F', letterSpacing:'-0.1px' }}>
            <b>{a.username}</b> {isSpeed ? `a ${a.speedKmh} km/h (límite ${a.limitKmh} km/h)` : `${isExit ? 'salió de' : 'entró a'} "${a.geofenceName}"`}
          </div>
          <div style={{ fontSize: 11, color: '#86868B', marginTop: 2, fontFamily:'var(--rt-font-mono)' }}>
            {new Date(a.timestamp).toLocaleTimeString()}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {onLocate && (a.lat != null || a.userId) && (
            <button onClick={() => { onLocate(a); onDismiss(0); }} style={{ background: '#E6F1FF', border: 'none', borderRadius: 8, padding: '5px 12px', color: '#007AFF', cursor: 'pointer', fontSize: 12, fontWeight:700, whiteSpace:'nowrap' }}>
              📍 Ver en mapa
            </button>
          )}
          <button onClick={() => onDismiss(0)} style={{ background: '#F5F5F7', border: 'none', borderRadius: 8, padding: '5px 12px', color: '#48484A', cursor: 'pointer', fontSize: 12, fontWeight:600 }}>
            Cerrar
          </button>
          {alarms.length > 1 && (
            <div style={{ fontSize: 10, color: '#86868B', textAlign: 'center', fontWeight:600 }}>+{alarms.length - 1} más</div>
          )}
        </div>
      </div>
    </>
  );
}

// ── Helpers ────────────────────────────────────────────────────
function headingLabel(h) {
  if (h == null) return null;
  return ['N','NE','E','SE','S','SO','O','NO'][Math.round(h / 45) % 8];
}


// Get initials from username (e.g. "Robert Muesser" -> "RM", "juan" -> "J")
function getInitials(username) {
  if (!username) return '?';
  const parts = username.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length-1][0]).toUpperCase();
  return username.slice(0, 2).toUpperCase();
}

function createUserIcon(color, isTransmitting, isPanic, heading, isMoving, isStopped, username, mapEmoji) {
  const glow = isPanic ? '0 0 18px #FF2D55,0 0 6px #FF2D55'
    : isTransmitting ? '0 0 14px #FF375F'
    : `0 0 12px ${color}bb`;
  const spin = isTransmitting ? 'animation:rtspin 2s linear infinite;' : '';
  const pAnim = isPanic ? 'animation:rtpanic 0.4s ease-in-out infinite alternate;' : '';
  const emoji = isPanic ? '🚨' : (mapEmoji || '🐯');
  const initials = getInitials(username || '');
  const arrow = isMoving && heading != null
    ? `<div style="position:absolute;top:-18px;left:50%;transform:translateX(-50%) rotate(${heading}deg);font-size:13px;color:${color};">▲</div>` : '';
  const stopBadge = isStopped
    ? `<div style="position:absolute;bottom:-10px;left:50%;transform:translateX(-50%);background:#f59e0b;color:#050810;font-size:7px;padding:1px 4px;border-radius:3px;white-space:nowrap;font-weight:700;letter-spacing:0.03em;">DETENIDO</div>` : '';
  // Circle with initials + emoji
  const markerBody = `<div style="width:46px;height:46px;border-radius:50%;background:${color};display:flex;flex-direction:column;align-items:center;justify-content:center;filter:drop-shadow(${glow});${spin}${pAnim}border:2px solid rgba(255,255,255,0.18);gap:0;">
    <div style="font-size:9px;font-weight:800;color:#050810;line-height:1;letter-spacing:0.05em;">${initials}</div>
    <div style="font-size:18px;line-height:1;">${emoji}</div>
  </div>`;
  return L.divIcon({
    html: `<div style="position:relative;width:46px;height:64px;">${arrow}${markerBody}${stopBadge}</div><style>@keyframes rtspin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}@keyframes rtpanic{from{transform:scale(1)}to{transform:scale(1.3)}}</style>`,
    iconSize: [46, 64], iconAnchor: [23, 46], popupAnchor: [0, -48], className: '',
  });
}

function createPolyVertexIcon(index) {
  return L.divIcon({
    html: `<div style="width:12px;height:12px;background:#AF52DE;border:2px solid #fff;border-radius:50%;box-shadow:0 2px 6px rgba(0,0,0,.2);"></div>`,
    iconSize: [12,12], iconAnchor: [6,6], className: '',
  });
}

function createWaypointIcon(e) {
  return L.divIcon({ html: `<div style="font-size:22px;">${e}</div>`, iconSize:[24,24], iconAnchor:[12,12], className:'' });
}
function createDispatchPinIcon() {
  return L.divIcon({ html: `<div style="font-size:28px;">📌</div>`, iconSize:[28,28], iconAnchor:[14,28], className:'' });
}

function timeAgo(ts) {
  if (!ts) return 'Nunca';
  const d = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (d < 60) return `${d}s`;
  if (d < 3600) return `${Math.floor(d/60)}min`;
  if (d < 86400) return `${Math.floor(d/3600)}h`;
  return `${Math.floor(d/86400)}d`;
}

function parseUA(ua) {
  if (!ua) return '?';
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Edg')) return 'Edge';
  if (ua.includes('Chrome')) return 'Chrome';
  if (ua.includes('Safari')) return 'Safari';
  return 'Browser';
}
function parseOS(ua, p) {
  if (!ua) return p || '?';
  if (ua.includes('Android')) return 'Android';
  if (ua.includes('iPhone')||ua.includes('iPad')) return 'iOS';
  if (ua.includes('Windows')) return 'Windows';
  if (ua.includes('Mac')) return 'macOS';
  if (ua.includes('Linux')) return 'Linux';
  return p || '?';
}

function FitBounds({ locations }) {
  const map = useMap();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    const v = locations.filter(l => l.lat && l.lng);
    if (!v.length) return;
    v.length === 1 ? map.setView([v[0].lat, v[0].lng], 13) : map.fitBounds(L.latLngBounds(v.map(l=>[l.lat,l.lng])), { padding:[50,50] });
    done.current = true;
  }, [locations, map]);
  return null;
}

function FlyTo({ target }) {
  const map = useMap();
  useEffect(() => { if (target) map.flyTo([target.lat, target.lng], 16, { duration: 1 }); }, [target, map]);
  return null;
}

// Handles clicks for geofence drawing and dispatch
function MapInteraction({ drawMode, drawType, onMapClick, onMapDblClick, dispatchMode, onDispatch }) {
  useMapEvents({
    click(e) {
      if (drawMode) onMapClick(e.latlng);
      else if (dispatchMode) onDispatch(e.latlng);
    },
    dblclick(e) {
      if (drawMode && drawType === 'polygon') onMapDblClick(e.latlng);
    },
  });
  return null;
}

// Trail renderer — fading polyline of last 60 positions (~4 min)
// Splits into 4 segments for a hand-drawn fade effect (oldest segment most transparent).
function UserTrails({ trailsRef, windowMs }) {
  const [trails, setTrails] = useState({});
  useEffect(() => {
    const t = setInterval(() => {
      const now = Date.now(); const next = {};
      for (const [uid, pts] of Object.entries(trailsRef.current)) {
        let fresh = pts.filter(p => now - p.ts <= (windowMs || TRAIL_MS));
        if (fresh.length > TRAIL_MAX_POINTS) fresh = fresh.slice(-TRAIL_MAX_POINTS);
        if (fresh.length > 1) next[uid] = fresh;
        trailsRef.current[uid] = fresh;
      }
      setTrails({ ...next });
    }, 800);
    return () => clearInterval(t);
  }, [trailsRef, windowMs]);
  return <>
    {Object.entries(trails).map(([uid, pts]) => {
      if (pts.length < 2) return null;
      const color = pts[0].color || '#007AFF';
      const SEGMENTS = 4;
      const len = pts.length;
      return (
        <React.Fragment key={uid}>
          {Array.from({ length: SEGMENTS }).map((_, i) => {
            const a = Math.floor((len - 1) * (i / SEGMENTS));
            const b = Math.floor((len - 1) * ((i + 1) / SEGMENTS)) + 1;
            const seg = pts.slice(a, b);
            if (seg.length < 2) return null;
            // Older segments → lower opacity, thinner stroke
            const opacity = 0.18 + (i / (SEGMENTS - 1)) * 0.65;
            const weight = 2 + i;
            return (
              <Polyline
                key={i}
                positions={seg.map(p => [p.lat, p.lng])}
                pathOptions={{ color, weight, opacity }}
              />
            );
          })}
        </React.Fragment>
      );
    })}
  </>;
}

// Route replay
function RouteReplay({ points }) {
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const map = useMap();
  const timerRef = useRef(null);
  useEffect(() => {
    if (playing && idx < points.length - 1) {
      timerRef.current = setTimeout(() => {
        setIdx(i => i + 1);
        map.panTo([points[idx].lat, points[idx].lng], { animate: false });
      }, 300);
    } else if (idx >= points.length - 1) setPlaying(false);
    return () => clearTimeout(timerRef.current);
  }, [playing, idx, points, map]);
  if (!points.length) return null;
  const cur = points[idx];
  const sp = cur.speed != null ? (cur.speed * 3.6).toFixed(0) : '--';
  const pct = Math.round((idx / Math.max(points.length-1,1)) * 100);
  return (
    <div style={{
      position:'absolute', bottom:20, left:'50%', transform:'translateX(-50%)', zIndex:1000,
      background:'rgba(255,255,255,0.92)',
      WebkitBackdropFilter:'saturate(180%) blur(30px)',
      backdropFilter:'saturate(180%) blur(30px)',
      border:'1px solid rgba(255,255,255,0.7)',
      borderRadius:14, padding:'12px 18px',
      display:'flex', flexDirection:'column', gap:7, minWidth:300,
      boxShadow:'0 20px 50px rgba(0,0,0,.14), 0 4px 12px rgba(0,0,0,.06)',
    }}>
      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
        <button onClick={() => { setIdx(0); setPlaying(false); }} style={btnSm}>⏮</button>
        <button onClick={() => setPlaying(p => !p)} style={{
          ...btnSm,
          background:'linear-gradient(135deg,#007AFF,#5856D6)',
          color:'#fff',
          boxShadow:'0 4px 10px rgba(0,122,255,.30)',
          border:'none',
        }}>{playing ? '⏸' : '▶'}</button>
        <input type="range" min={0} max={points.length-1} value={idx} onChange={e => { setIdx(+e.target.value); setPlaying(false); }} style={{ flex:1, accentColor:'#007AFF' }} />
        <span style={{ fontSize:11, color:'#48484A', fontWeight:700, fontFamily:'var(--rt-font-mono)' }}>{pct}%</span>
      </div>
      <div style={{ display:'flex', gap:16, fontSize:11, color:'#86868B', fontFamily:'var(--rt-font-mono)' }}>
        <span>🕐 {new Date(cur.recordedAt).toLocaleTimeString()}</span>
        <span>🚀 {sp} km/h</span>
        <span>📍 {idx+1}/{points.length}</span>
      </div>
    </div>
  );
}


const TILES = {
  dark:      { url: 'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}', label: '🌑 Oscuro' },
  satellite: { url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', label: '🛰 Satélite' },
  street:    { url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png', label: '🗺 Calles' },
};

const btnSm = { background:'#F5F5F7', border:'none', borderRadius:8, color:'#48484A', padding:'5px 10px', cursor:'pointer', fontSize:13, fontWeight:600 };

// ── Main Component ─────────────────────────────────────────────
export default function MapPage() {
  const navigate = useNavigate();
  const currentUser = useStore(s => s.user);
  const [users, setUsers] = useState([]);
  const [panicAlerts, setPanicAlerts] = useState([]);
  const [filter, setFilter] = useState('all');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [loading, setLoading] = useState(true);
  const [flyTo, setFlyTo] = useState(null);
  const isAdmin = currentUser?.role === 'admin';



  // ── Map tile / offline ────────────────────────────────────────
  const [tileStyle, setTileStyle] = useState('dark'); // dark | satellite | street
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  useEffect(() => {
    const onOnline = () => setIsOffline(false);
    const onOffline = () => setIsOffline(true);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => { window.removeEventListener('online', onOnline); window.removeEventListener('offline', onOffline); };
  }, []);

  // ── Floating PTT ──────────────────────────────────────────────
  const { startTransmitting, stopTransmitting, requestPermission, permissionGranted } = usePTT();
  const currentChannel = useStore(s => s.currentChannel);
  const isTransmitting = useStore(s => s.isTransmitting);
  const transmittingUser = useStore(s => s.transmittingUser);
  const [pttPressed, setPttPressed] = useState(false);
  const pttPressedRef = useRef(false);

  const handlePttDown = useCallback(async () => {
    if (pttPressedRef.current) return;
    if (!permissionGranted) { await requestPermission(); return; }
    if (!currentChannel) return;
    pttPressedRef.current = true;
    setPttPressed(true);
    startTransmitting();
  }, [permissionGranted, requestPermission, currentChannel, startTransmitting]);

  const handlePttUp = useCallback(() => {
    if (!pttPressedRef.current) return;
    pttPressedRef.current = false;
    setPttPressed(false);
    stopTransmitting();
  }, [stopTransmitting]);

  const trailsRef = useRef({});
  const locationMapRef = useRef({});
  const lastMoveRef = useRef({});

  // Alarm system
  const [activeAlarms, setActiveAlarms] = useState([]);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const dismissAlarm = (index) => setActiveAlarms(prev => prev.filter((_, i) => i !== index));
  const triggerAlarm = useCallback((alarm) => {
    setActiveAlarms(prev => [alarm, ...prev].slice(0, 5));
    if (audioEnabled) playAlarmSound(alarm.alertType || alarm.type);
    // Auto-dismiss after 8 seconds
    setTimeout(() => setActiveAlarms(prev => prev.filter(a => a !== alarm)), 8000);
  }, [audioEnabled]);

  // Panels
  const [activePanel, setActivePanel] = useState(null);
  const togglePanel = (p) => setActivePanel(v => v === p ? null : p);

  // Estela window (minutes) — reseeds the trail from history on change
  const [trailMinutes, setTrailMinutes] = useState(30);
  useEffect(() => {
    axios.get(`/api/map/history-recent/all?minutes=${trailMinutes}`).then(r => {
      const now = Date.now();
      const next = {};
      for (const [uid, pts] of Object.entries(r.data || {})) {
        const seeded = pts
          .filter(p => now - p.ts <= trailMinutes * 60000)
          .map(p => ({ lat: p.lat, lng: p.lng, ts: p.ts, color: p.color }));
        if (seeded.length) next[uid] = seeded;
      }
      trailsRef.current = next;
    }).catch(() => {});
  }, [trailMinutes]); // eslint-disable-line react-hooks/exhaustive-deps

  // History
  const [historyUserId, setHistoryUserId] = useState('');
  const [historyDate, setHistoryDate] = useState(new Date().toISOString().slice(0,10));
  const [historyPoints, setHistoryPoints] = useState([]);
  const [historyStats, setHistoryStats] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [replayMode, setReplayMode] = useState(false);
  const [todayStats, setTodayStats] = useState({});

  // Geofences
  const [geofences, setGeofences] = useState([]);
  const [drawMode, setDrawMode] = useState(false);
  const [drawType, setDrawType] = useState('circle'); // 'circle' | 'polygon'
  const [newGfCenter, setNewGfCenter] = useState(null);     // circle
  const [polyPoints, setPolyPoints] = useState([]);          // polygon vertices
  const [newGfForm, setNewGfForm] = useState({ name:'', radius:200, color:'#00D4FF', alertOnEnter:true, alertOnExit:true, speedLimit:'', autoChannelId:'' });
  const [gfEventFeed, setGfEventFeed] = useState([]);
  const [channels, setChannels] = useState([]);

  // Convoy
  const [convoyChan, setConvoyChan] = useState('');

  // Dispatch
  const [dispatchMode, setDispatchMode] = useState(false);
  const [dispatchPin, setDispatchPin] = useState(null);
  const [dispatchResults, setDispatchResults] = useState([]);

  // Speed alert feed
  const [speedAlerts, setSpeedAlerts] = useState([]);

  // Telemetry slide-in panel
  const [telemetryUserId, setTelemetryUserId] = useState(null);

  // ── Fetchers ──────────────────────────────────────────────────
  const fetchUsers = useCallback(async () => {
    try {
      const [ur, ar] = await Promise.all([
        axios.get('/api/map/users'),
        isAdmin ? axios.get('/api/map/panic-alerts').catch(() => ({ data:[] })) : Promise.resolve({ data:[] }),
      ]);
      setUsers(ur.data.map(u => ({ ...u, ...(locationMapRef.current[u.id]||{}) })));
      setPanicAlerts(ar.data);
      setLastUpdated(new Date());
    } catch (_) {} finally { setLoading(false); }
  }, [isAdmin]);

  const fetchGeofences = useCallback(async () => {
    // All authenticated users can see geofences (not just admin)
    try { const r = await axios.get('/api/map/geofences'); setGeofences(r.data); } catch (_) {}
  }, []);

  const fetchChannels = useCallback(async () => {
    try { const r = await axios.get('/api/channels'); setChannels(r.data); } catch (_) {}
  }, []);

  const fetchTodayStats = useCallback(async (uid) => {
    if (!isAdmin) return;
    try { const r = await axios.get(`/api/map/history/${uid}/today`); setTodayStats(p => ({ ...p, [uid]: r.data })); } catch (_) {}
  }, [isAdmin]);

  const loadHistoryForUser = useCallback(async (uid, date) => {
    const u = uid||historyUserId, d = date||historyDate;
    if (!u) return;
    setHistoryLoading(true); setHistoryPoints([]); setHistoryStats(null); setReplayMode(false);
    try {
      const [pr, sr] = await Promise.all([
        axios.get(`/api/map/history/${u}?from=${d}T00:00:00&to=${d}T23:59:59&limit=2000`),
        axios.get(`/api/map/history/${u}/today`),
      ]);
      setHistoryPoints(pr.data); setHistoryStats(sr.data);
      if (pr.data.length) setFlyTo({ lat:pr.data[0].lat, lng:pr.data[0].lng });
    } catch (_) {} finally { setHistoryLoading(false); }
  }, [historyUserId, historyDate]);

  const loadHistory = () => loadHistoryForUser(historyUserId, historyDate);
  const exportGPX = (uid) => {
    const u = uid||historyUserId; if (!u) return;
    window.open(`/api/map/history/${u}/export.gpx?from=${historyDate}T00:00:00&to=${historyDate}T23:59:59`, '_blank');
  };

  // Geofence creation
  const cancelDraw = () => { setDrawMode(false); setNewGfCenter(null); setPolyPoints([]); };

  const handleMapClick = (latlng) => {
    if (drawType === 'circle') {
      setNewGfCenter({ lat: latlng.lat, lng: latlng.lng });
    } else {
      setPolyPoints(prev => [...prev, { lat: latlng.lat, lng: latlng.lng }]);
    }
  };

  const handleMapDblClick = (latlng) => {
    if (drawType === 'polygon' && polyPoints.length >= 3) finishPolygon();
  };

  const finishPolygon = () => {
    if (polyPoints.length < 3) return;
    // Just mark as ready — form will handle save
    setNewGfCenter({ lat: polyPoints[0].lat, lng: polyPoints[0].lng }); // signal "ready"
  };

  const createGeofence = async () => {
    const isCircleReady = drawType === 'circle' && newGfCenter && newGfForm.name;
    const isPolyReady = drawType === 'polygon' && polyPoints.length >= 3 && newGfForm.name;
    if (!isCircleReady && !isPolyReady) return;
    try {
      await axios.post('/api/map/geofences', {
        name: newGfForm.name,
        type: drawType,
        coordinates: drawType === 'circle' ? [{ lat: newGfCenter.lat, lng: newGfCenter.lng }] : polyPoints,
        radius: drawType === 'circle' ? parseFloat(newGfForm.radius) : null,
        color: newGfForm.color,
        alertOnEnter: newGfForm.alertOnEnter,
        alertOnExit: newGfForm.alertOnExit,
        speedLimit: newGfForm.speedLimit ? parseFloat(newGfForm.speedLimit) : null,
        autoChannelId: newGfForm.autoChannelId || null,
      });
      cancelDraw();
      setNewGfForm({ name:'', radius:200, color:'#00D4FF', alertOnEnter:true, alertOnExit:true, speedLimit:'', autoChannelId:'' });
      fetchGeofences();
    } catch (_) {}
  };

  const deleteGeofence = async (id) => {
    try { await axios.delete(`/api/map/geofences/${id}`); fetchGeofences(); } catch (_) {}
  };

  const runDispatch = async (ll) => {
    try { const r = await axios.post('/api/map/dispatch/nearest', { lat:ll.lat, lng:ll.lng, limit:5 }); setDispatchResults(r.data); } catch (_) {}
  };

  // ── Socket ────────────────────────────────────────────────────
  useEffect(() => {
    fetchUsers(); fetchGeofences(); fetchChannels();
    const socket = getSocket();
    if (socket?.connected) socket.emit('map:requestAll');

    const onLoc = (data) => {
      const now = Date.now();
      if (!trailsRef.current[data.userId]) trailsRef.current[data.userId] = [];
      trailsRef.current[data.userId].push({ lat:data.lat, lng:data.lng, ts:now, color:data.avatarColor||'#00D4FF' });
      if (data.speed != null && data.speed > 0.5) lastMoveRef.current[data.userId] = now;
      locationMapRef.current[data.userId] = { lat:data.lat, lng:data.lng, speed:data.speed, heading:data.heading, accuracy:data.accuracy, mapEmoji:data.mapEmoji, lastLocationAt:new Date(data.timestamp).toISOString() };
      setUsers(prev => prev.map(u => u.id === data.userId ? { ...u, ...locationMapRef.current[u.id] } : u));
    };
    const onAllLocs = (locs) => {
      locs.forEach(l => { locationMapRef.current[l.userId] = { lat:l.lat, lng:l.lng, speed:l.speed, heading:l.heading, accuracy:l.accuracy, lastLocationAt:new Date(l.timestamp).toISOString() }; });
      setUsers(prev => prev.map(u => ({ ...u, ...(locationMapRef.current[u.id]||{}) })));
    };
    const onPanic = (a) => { setPanicAlerts(prev => [a,...prev]); triggerAlarm({ ...a, alertType:'panic', type:'exit', username: a.username||'Usuario', geofenceName:'PÁNICO', timestamp:Date.now() }); };
    const onGfEvent = (e) => {
      setGfEventFeed(prev => [e,...prev].slice(0,50));
      // Only trigger alarm for exit events (or enter if alertOnEnter)
      triggerAlarm({ ...e, alertType: e.type });
    };
    const onSpeedAlert = (a) => {
      setSpeedAlerts(prev => [a,...prev].slice(0,20));
      triggerAlarm({ ...a, alertType:'speed', type:'speed' });
    };

    if (socket) {
      socket.on('user:locationUpdate', onLoc);
      socket.on('map:allLocations', onAllLocs);
      socket.on('panic:newAlert', onPanic);
      socket.on('geofence:event', onGfEvent);
      socket.on('speed:alert', onSpeedAlert);
    }
    const iv = setInterval(fetchUsers, 30000);
    return () => {
      clearInterval(iv);
      if (socket) {
        socket.off('user:locationUpdate', onLoc);
        socket.off('map:allLocations', onAllLocs);
        socket.off('panic:newAlert', onPanic);
        socket.off('geofence:event', onGfEvent);
        socket.off('speed:alert', onSpeedAlert);
      }
    };
  }, [fetchUsers, fetchChannels, triggerAlarm]);


  // Re-fetch geofences when user loads (fixes persistence bug)
  useEffect(() => {
    if (currentUser) fetchGeofences();
  }, [currentUser?.id, fetchGeofences]);
  // ── Derived ───────────────────────────────────────────────────
  const filteredUsers = users.filter(u => filter==='online'?u.isOnline:filter==='located'?u.lat&&u.lng:true);
  const locatedUsers = filteredUsers.filter(u => u.lat&&u.lng);
  const activePanics = panicAlerts.filter(a => !a.resolvedAt);
  const isStopped = (u) => { const lm = lastMoveRef.current[u.id]; return lm && (Date.now()-lm)>STOP_THRESHOLD_MS; };
  const convoyUsers = convoyChan ? locatedUsers.filter(u => u.isOnline) : [];

  const resolveAlert = async (id) => {
    try { await axios.patch(`/api/map/panic-alerts/${id}/resolve`); setPanicAlerts(prev => prev.map(a => a.id===id ? {...a, resolvedAt:new Date().toISOString()} : a)); } catch (_) {}
  };

  // ── Form readiness ─────────────────────────────────────────────
  const circleReady = drawType === 'circle' && newGfCenter;
  const polyReady = drawType === 'polygon' && polyPoints.length >= 3;
  const formReady = (circleReady || polyReady) && newGfForm.name;

  // ── Panel renderer ─────────────────────────────────────────────
  const renderPanel = () => {
    if (activePanel === 'history') return (
      <div style={panelStyle}>
        <PanelHeader title="📊 Historial de Ruta" onClose={() => { setActivePanel(null); setHistoryPoints([]); setHistoryStats(null); setReplayMode(false); }} />
        <select value={historyUserId} onChange={e=>setHistoryUserId(e.target.value)} style={inputStyle}>
          <option value="">— Usuario —</option>
          {users.map(u=><option key={u.id} value={u.id}>{u.username}</option>)}
        </select>
        <input type="date" value={historyDate} onChange={e=>setHistoryDate(e.target.value)} style={{ ...inputStyle, marginTop:6 }} />
        <div style={{ display:'flex', gap:5, marginTop:8 }}>
          <button onClick={loadHistory} disabled={!historyUserId||historyLoading} style={historyUserId?primaryBtn:disabledBtn}>{historyLoading?'...':'▶ Ver ruta'}</button>
          {historyPoints.length > 0 && (
            <button onClick={()=>setReplayMode(v=>!v)} style={{ ...primaryBtn, background: replayMode ? 'linear-gradient(135deg,#FF9F0A,#FF9500)' : 'linear-gradient(135deg,#007AFF,#5856D6)', boxShadow: replayMode ? '0 4px 10px rgba(255,159,10,.30)' : '0 4px 10px rgba(0,122,255,.30)' }}>{replayMode?'⏹ Stop':'🎬 Replay'}</button>
          )}
          <button onClick={()=>exportGPX(historyUserId)} disabled={!historyUserId} title="GPX" style={secondaryBtn}>⬇️</button>
        </div>
        {historyStats && historyPoints.length > 0 && (
          <div style={statBox}>
            <div style={statLabel}>Estadísticas</div>
            <div style={statRow}>📍 {historyPoints.length} puntos · 📏 {historyStats.totalDistanceKm} km</div>
            <div style={statRow}>⚡ Prom {historyStats.avgSpeedKmh} · Máx {historyStats.maxSpeedKmh} km/h</div>
          </div>
        )}
        {historyStats !== null && !historyPoints.length && !historyLoading && <div style={{ fontSize:11, color:'#86868B', textAlign:'center', marginTop:8 }}>Sin datos para esta fecha</div>}
      </div>
    );

    if (activePanel === 'geofences') return (
      <div style={panelStyle}>
        <PanelHeader title="🔵 Geocercas" onClose={() => { setActivePanel(null); cancelDraw(); }} />

        {isAdmin && !drawMode && (
          <div style={{ display:'flex', gap:5, marginBottom:8 }}>
            <button onClick={() => { setDrawMode(true); setDrawType('circle'); }} style={{ ...primaryBtn, flex:1, fontSize:11 }}>⭕ Círculo</button>
            <button onClick={() => { setDrawMode(true); setDrawType('polygon'); }} style={{ ...primaryBtn, flex:1, fontSize:11, background:'linear-gradient(135deg,#AF52DE,#FF2D55)', boxShadow:'0 4px 10px rgba(175,82,222,.30)' }}>⬡ Polígono</button>
          </div>
        )}

        {drawMode && (
          <div style={{ marginBottom:8 }}>
            {/* Instructions */}
            {drawType === 'circle' && !newGfCenter && (
              <div style={instructionBox('#FF9500')}>📍 Click en el mapa para colocar el centro</div>
            )}
            {drawType === 'polygon' && polyPoints.length < 3 && (
              <div style={instructionBox('#AF52DE')}>⬡ Click para agregar vértices ({polyPoints.length}/3 mín) · Doble-click para cerrar</div>
            )}
            {drawType === 'polygon' && polyPoints.length >= 3 && (
              <div style={instructionBox('#34C759')}>✓ {polyPoints.length} puntos · Click para más · Doble-click para cerrar</div>
            )}

            {/* Form (show when ready to name) */}
            {(circleReady || polyPoints.length >= 3) && (
              <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
                <input placeholder="Nombre de la geocerca *" value={newGfForm.name} onChange={e=>setNewGfForm(p=>({...p,name:e.target.value}))} style={inputStyle} />
                {drawType === 'circle' && (
                  <div style={{ display:'flex', gap:5 }}>
                    <input placeholder="Radio (m)" type="number" value={newGfForm.radius} onChange={e=>setNewGfForm(p=>({...p,radius:e.target.value}))} style={{ ...inputStyle, flex:1 }} />
                    <input type="color" value={newGfForm.color} onChange={e=>setNewGfForm(p=>({...p,color:e.target.value}))} style={{ width:36, height:32, borderRadius:8, border:'1px solid rgba(0,0,0,0.10)', background:'none', cursor:'pointer' }} />
                  </div>
                )}
                {drawType === 'polygon' && (
                  <div style={{ display:'flex', gap:5, alignItems:'center' }}>
                    <span style={{ fontSize:11, color:'#48484A', flex:1, fontWeight:600 }}>{polyPoints.length} vértices</span>
                    <input type="color" value={newGfForm.color} onChange={e=>setNewGfForm(p=>({...p,color:e.target.value}))} style={{ width:36, height:32, borderRadius:8, border:'1px solid rgba(0,0,0,0.10)', background:'none', cursor:'pointer' }} />
                  </div>
                )}
                <input placeholder="Vel. máx km/h (opcional)" type="number" value={newGfForm.speedLimit} onChange={e=>setNewGfForm(p=>({...p,speedLimit:e.target.value}))} style={inputStyle} />
                <select value={newGfForm.autoChannelId} onChange={e=>setNewGfForm(p=>({...p,autoChannelId:e.target.value}))} style={inputStyle}>
                  <option value="">Sin canal automático</option>
                  {channels.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <div style={{ display:'flex', gap:6 }}>
                  <label style={checkLabel}><input type="checkbox" checked={newGfForm.alertOnEnter} onChange={e=>setNewGfForm(p=>({...p,alertOnEnter:e.target.checked}))} /> Alarma entrada</label>
                  <label style={checkLabel}><input type="checkbox" checked={newGfForm.alertOnExit} onChange={e=>setNewGfForm(p=>({...p,alertOnExit:e.target.checked}))} /> Alarma salida</label>
                </div>
                <div style={{ display:'flex', gap:5 }}>
                  <button onClick={createGeofence} disabled={!formReady} style={formReady?primaryBtn:disabledBtn}>✓ Crear</button>
                  <button onClick={cancelDraw} style={secondaryBtn}>✕ Cancelar</button>
                </div>
              </div>
            )}
            {drawMode && !circleReady && polyPoints.length < 3 && (
              <button onClick={cancelDraw} style={{ ...secondaryBtn, width:'100%', marginTop:4 }}>✕ Cancelar</button>
            )}
          </div>
        )}

        {/* Geofence list */}
        <div className="scrollbar-thin" style={{ maxHeight:200, overflowY:'auto' }}>
          {geofences.length === 0 && <div style={{ fontSize:11, color:'#86868B', textAlign:'center', padding:'10px 0' }}>Sin geocercas</div>}
          {geofences.map(gf => (
            <div key={gf.id} style={{ padding:'8px 0', borderBottom:'1px solid rgba(0,0,0,0.06)', display:'flex', alignItems:'center', gap:8 }}>
              <div style={{ fontSize:13 }}>{gf.type==='polygon'?'⬡':'⭕'}</div>
              <div style={{ width:8, height:8, borderRadius:'50%', background:gf.color, flexShrink:0 }} />
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:13, color:'#1D1D1F', fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', letterSpacing:'-0.1px' }}>{gf.name}</div>
                <div style={{ fontSize:10, color:'#86868B' }}>
                  {gf.type==='circle'?`${gf.radius}m`:'Polígono'} {gf.speedLimit?`· ≤${gf.speedLimit}km/h`:''} {gf.alertOnEnter&&gf.alertOnExit?'· 🔔':gf.alertOnExit?'· 🔔 salida':gf.alertOnEnter?'· 🔔 entrada':''}
                </div>
              </div>
              {isAdmin && <button onClick={()=>deleteGeofence(gf.id)} style={{ background:'none', border:'none', color:'#FF3B30', cursor:'pointer', fontSize:13, padding:'0 4px', fontWeight:700 }}>✕</button>}
            </div>
          ))}
        </div>

        {/* Event feed */}
        {gfEventFeed.length > 0 && (
          <div style={{ borderTop:'1px solid rgba(0,0,0,0.06)', paddingTop:8, marginTop:6 }}>
            <div style={statLabel}>Eventos recientes</div>
            {gfEventFeed.slice(0,6).map((e,i) => (
              <div key={i} style={{ fontSize:11, color:e.type==='enter'?'#34C759':'#FF3B30', padding:'2px 0', display:'flex', gap:6, fontWeight:600 }}>
                <span>{e.type==='enter'?'→':'←'}</span>
                <span style={{ flex:1, color:'#1D1D1F' }}>{e.username} / <b>{e.geofenceName}</b></span>
                <span style={{ color:'#86868B', fontFamily:'var(--rt-font-mono)' }}>{timeAgo(e.timestamp)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );

    if (activePanel === 'flota') {
      // Operational fleet list: status at a glance, click = center map
      const fleet = [...users].filter(u => u.username).sort((a, b) => {
        const rank = u => (u.isOnline && u.lat != null) ? 0 : u.isOnline ? 1 : 2;
        return rank(a) - rank(b) || a.username.localeCompare(b.username);
      });
      return (
        <div style={panelStyle}>
          <PanelHeader title="🚛 Flota" onClose={()=>setActivePanel(null)} />
          {fleet.map(u => {
            const moving = u.isOnline && u.speed != null && u.speed > 0.5;
            const status = !u.isOnline ? { dot:'#AEAEB2', label:'Offline' }
              : moving ? { dot:'#34C759', label:`${(u.speed*3.6).toFixed(0)} km/h` }
              : { dot:'#FF9500', label:'Detenido' };
            const seen = u.lastLocationAt ? timeAgo(new Date(u.lastLocationAt).getTime()) : 'sin ubicación';
            return (
              <div key={u.id} style={{ padding:'8px 0', borderBottom:'1px solid rgba(0,0,0,0.06)', display:'flex', alignItems:'center', gap:10, cursor:u.lat!=null?'pointer':'default', opacity:u.isOnline?1:0.55 }}
                onClick={()=>{ if (u.lat!=null) setFlyTo({lat:u.lat,lng:u.lng}); }}>
                <div style={{ position:'relative' }}>
                  <div style={{
                    width:26,height:26,borderRadius:'50%',
                    background:`linear-gradient(135deg, ${u.avatarColor||'#007AFF'}, #AF52DE)`,
                    display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontWeight:700,fontSize:10,
                  }}>{(u.username?.[0]||'?').toUpperCase()}</div>
                  <div style={{ position:'absolute', bottom:-1, right:-1, width:9, height:9, borderRadius:'50%', background:status.dot, border:'2px solid #fff' }} />
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:12, color:'#1D1D1F', fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{u.username}</div>
                  <div style={{ fontSize:10, color:'#86868B' }}>{status.label} · {seen}</div>
                </div>
                {u.lat != null && <span style={{ fontSize:13 }}>📍</span>}
              </div>
            );
          })}
          {!fleet.length && <div style={{ fontSize:11, color:'#86868B' }}>Sin usuarios</div>}
        </div>
      );
    }

    if (activePanel === 'convoy') return (
      <div style={panelStyle}>
        <PanelHeader title="🚗 Modo Convoy" onClose={()=>setActivePanel(null)} />
        <select value={convoyChan} onChange={e=>setConvoyChan(e.target.value)} style={{ ...inputStyle, marginBottom:8 }}>
          <option value="">— Seleccionar canal —</option>
          {channels.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        {convoyChan && convoyUsers.map((u,i) => {
          let distStr='--';
          if (i>0&&convoyUsers[0].lat) {
            const R=6371000,r=d=>d*Math.PI/180;
            const a=Math.sin(r(u.lat-convoyUsers[0].lat)/2)**2+Math.cos(r(convoyUsers[0].lat))*Math.cos(r(u.lat))*Math.sin(r(u.lng-convoyUsers[0].lng)/2)**2;
            const d=R*2*Math.atan2(Math.sqrt(a),Math.sqrt(1-a));
            distStr=d<1000?`${Math.round(d)}m`:`${(d/1000).toFixed(1)}km`;
          }
          const sp=u.speed!=null?(u.speed*3.6).toFixed(0):'--';
          return (
            <div key={u.id} style={{ padding:'8px 0', borderBottom:'1px solid rgba(0,0,0,0.06)', display:'flex', alignItems:'center', gap:10 }}>
              <div style={{
                width:24,height:24,borderRadius:'50%',
                background:`linear-gradient(135deg, ${u.avatarColor||'#007AFF'}, #AF52DE)`,
                display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontWeight:700,fontSize:10,
                boxShadow:'0 1px 2px rgba(0,0,0,0.06)',
              }}>{(u.username?.[0]||'?').toUpperCase()}</div>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:12, color:'#1D1D1F', fontWeight:600 }}>{i===0?'👑 ':''}{u.username}</div>
                <div style={{ fontSize:10, color:'#86868B' }}>🚀 {sp} km/h {i>0?`· 📏 ${distStr} del líder`:'(líder)'}</div>
              </div>
              <button onClick={()=>setFlyTo({lat:u.lat,lng:u.lng})} style={secondaryBtn}>📍</button>
            </div>
          );
        })}
        {convoyChan && !convoyUsers.length && <div style={{ fontSize:11, color:'#86868B' }}>Sin usuarios con ubicación</div>}
      </div>
    );

    if (activePanel === 'dispatch') return (
      <div style={panelStyle}>
        <PanelHeader title="📡 Despacho Inteligente" onClose={()=>{ setActivePanel(null); setDispatchMode(false); setDispatchPin(null); setDispatchResults([]); }} />
        <button onClick={()=>setDispatchMode(v=>!v)} style={{
          ...primaryBtn, marginBottom:8,
          ...(dispatchMode ? { background:'linear-gradient(135deg,#FF9F0A,#FF9500)', boxShadow:'0 4px 10px rgba(255,159,10,.30)' } : {}),
        }}>
          {dispatchMode?'✕ Cancelar':'📌 Seleccionar punto en mapa'}
        </button>
        {dispatchMode && !dispatchPin && <div style={instructionBox('#FF9500')}>Click en el mapa donde necesitas atención</div>}
        {dispatchResults.map((u,i) => (
          <div key={u.userId} style={{ padding:'8px 0', borderBottom:'1px solid rgba(0,0,0,0.06)', display:'flex', alignItems:'center', gap:10 }}>
            <div style={{
              width:20,height:20,borderRadius:'50%',
              background:`linear-gradient(135deg, ${u.avatarColor||'#007AFF'}, #AF52DE)`,
              display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontWeight:700,fontSize:10,flexShrink:0,
            }}>{i+1}</div>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:13, color:'#1D1D1F', fontWeight:600, letterSpacing:'-0.1px' }}>{u.username}</div>
              <div style={{ fontSize:10, color:'#86868B' }}>📏 {u.distanceKm} km {u.speedKmh!=null?`· 🚀 ${u.speedKmh} km/h`:'· Estático'}</div>
            </div>
            <button onClick={()=>setFlyTo({lat:u.lat,lng:u.lng})} style={secondaryBtn}>ver</button>
          </div>
        ))}
        {dispatchPin && !dispatchResults.length && <div style={{ fontSize:11, color:'#475569', textAlign:'center' }}>Sin agentes con ubicación</div>}
      </div>
    );
    return null;
  };

  // ── Render ────────────────────────────────────────────────────
  return (
    <div style={{ height:'100vh', width:'100vw', background:'#050810', display:'flex', flexDirection:'column', overflow:'hidden' }}>

      {/* Visual+audio alarm overlay */}
      <AlarmOverlay alarms={activeAlarms} onDismiss={dismissAlarm}
        onLocate={(a) => {
          if (a.lat != null && a.lng != null) return setFlyTo({ lat: a.lat, lng: a.lng });
          const loc = a.userId && locationMapRef.current[a.userId];
          if (loc) setFlyTo({ lat: loc.lat, lng: loc.lng });
        }} />

      {/* Header */}
      <div style={{
        height:56,
        background:'rgba(255,255,255,0.72)',
        WebkitBackdropFilter:'saturate(180%) blur(30px)',
        backdropFilter:'saturate(180%) blur(30px)',
        borderBottom:'1px solid rgba(0,0,0,0.06)',
        display:'flex', alignItems:'center', padding:'0 16px', gap:8, flexShrink:0, zIndex:1000,
      }}>
        <button onClick={()=>navigate('/')} style={headerBtn}><ArrowLeft size={14}/> Volver</button>
        <span style={{ fontSize:18 }}>🗺️</span>
        <span style={{ fontWeight:800, fontSize:15, color:'#1D1D1F', letterSpacing:'-0.3px' }}>Mapa en Tiempo Real</span>

        {activePanics.length > 0 && (
          <div style={{ background:'linear-gradient(135deg,#FF2D55,#FF3B30)', border:'none', borderRadius:999, padding:'5px 12px', display:'flex', alignItems:'center', gap:6, color:'#fff', fontSize:12, fontWeight:800, boxShadow:'0 6px 14px rgba(255,45,85,0.35)', animation:'breath 1.4s ease-in-out infinite' }}>
            <AlertTriangle size={13}/> {activePanics.length} ALERTA{activePanics.length>1?'S':''}
          </div>
        )}

        {(
          <div style={{ display:'flex', gap:4, marginLeft:4 }}>
            {[
              {id:'flota',icon:<Users size={13}/>,label:'Flota'},
              {id:'history',icon:<History size={13}/>,label:'Historial'},
              {id:'convoy',icon:<Users size={13}/>,label:'Convoy'},
              // Geofence CRUD and dispatch stay admin-only
              ...(isAdmin ? [
                {id:'geofences',icon:<Layers size={13}/>,label:'Geocercas'},
                {id:'dispatch',icon:<Navigation size={13}/>,label:'Despacho'},
              ] : []),
            ].map(b=>(
              <button key={b.id} onClick={()=>togglePanel(b.id)} style={{
                ...headerBtn,
                background: activePanel===b.id ? '#E6F1FF' : 'rgba(255,255,255,0.62)',
                border: 'none',
                color: activePanel===b.id ? '#007AFF' : '#1D1D1F',
                gap:4,
              }}>
                {b.icon}{b.label}
              </button>
            ))}
          </div>
        )}

        {/* Offline banner */}
        {isOffline && (
          <div style={{ background:'#FFE5EC', border:'1px solid rgba(255,45,85,0.20)', borderRadius:8, padding:'5px 10px', color:'#FF2D55', fontSize:12, fontWeight:700, display:'flex', alignItems:'center', gap:5 }}>
            📵 Sin conexión
          </div>
        )}

        <div style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:6 }}>
          {/* Estela window */}
          <div style={{ display:'flex', alignItems:'center', gap:4 }}>
            <span style={{ fontSize:11, color:'#86868B', fontWeight:600 }}>Estela</span>
            <select value={trailMinutes} onChange={e=>setTrailMinutes(Number(e.target.value))}
              style={{ fontSize:11, fontWeight:600, padding:'3px 6px', borderRadius:7, border:'1px solid rgba(0,0,0,0.08)', background:'rgba(255,255,255,0.62)', color:'#1D1D1F', cursor:'pointer' }}>
              <option value={10}>10 min</option>
              <option value={30}>30 min</option>
              <option value={60}>60 min</option>
            </select>
          </div>
          {/* Tile switcher */}
          <div style={{ display:'flex', gap:3 }}>
            {Object.entries(TILES).map(([k,t]) => (
              <button key={k} onClick={()=>setTileStyle(k)}
                style={{
                  ...headerBtn,
                  padding:'5px 9px', fontSize:10, fontWeight:700,
                  background: tileStyle===k ? '#1D1D1F' : '#F5F5F7',
                  border:'none',
                  color: tileStyle===k ? '#fff' : '#48484A',
                }}>
                {t.label}
              </button>
            ))}
          </div>
          {/* Audio toggle */}
          <button onClick={()=>setAudioEnabled(v=>!v)} title={audioEnabled?'Silenciar alarmas':'Activar alarmas'}
            style={{
              ...headerBtn,
              padding:'7px 9px',
              color: audioEnabled ? '#007AFF' : '#86868B',
              background: audioEnabled ? '#E6F1FF' : '#F5F5F7',
              border: 'none',
            }}>
            <Volume2 size={14}/>
          </button>
          {lastUpdated && <span style={{ fontSize:11, color:'#86868B', fontFamily:'var(--rt-font-mono)' }}>hace {timeAgo(lastUpdated)}</span>}
          <button onClick={fetchUsers} style={headerBtn}><RefreshCw size={14}/></button>
        </div>
      </div>

      {/* Main */}
      <div style={{ flex:1, display:'flex', overflow:'hidden' }}>
        {/* Map */}
        <div style={{ flex:1, position:'relative', minHeight:0 }}>
          {loading ? (
            <div style={{ height:'100%', display:'flex', alignItems:'center', justifyContent:'center', color:'#86868B', fontWeight:600, fontSize:14 }}>Cargando mapa…</div>
          ) : (
            <MapContainer center={[19.4326,-99.1332]} zoom={5} style={{ height:'calc(100vh - 56px)', width:'100%' }} zoomControl>
              <TileLayer key={tileStyle} url={TILES[tileStyle].url} attribution='&copy; OSM &copy; CARTO' subdomains="abcd" maxZoom={20}/>
              <FitBounds locations={locatedUsers}/>
              {flyTo && <FlyTo target={flyTo}/>}
              <MapInteraction drawMode={drawMode} drawType={drawType} onMapClick={handleMapClick} onMapDblClick={handleMapDblClick} dispatchMode={dispatchMode} onDispatch={(ll)=>{ setDispatchPin({lat:ll.lat,lng:ll.lng}); runDispatch(ll); setDispatchMode(false); }}/>

              <UserTrails trailsRef={trailsRef} windowMs={trailMinutes * 60000}/>

              {/* Geofences */}
              {geofences.map(gf => {
                if (gf.type === 'circle') {
                  const c = gf.coordinates[0];
                  return (
                    <Circle key={gf.id} center={[c.lat,c.lng]} radius={gf.radius} pathOptions={{ color:gf.color, fillColor:gf.color, fillOpacity:0.07, weight:1.5, opacity:0.6 }}>
                      <Popup><div style={{fontFamily:'sans-serif',fontSize:13}}><b>{gf.name}</b><br/>Radio: {gf.radius}m{gf.speedLimit?<><br/>Vel. máx: {gf.speedLimit} km/h</>:null}{gf.alertOnEnter||gf.alertOnExit?<><br/>🔔 Alarmas: {[gf.alertOnEnter&&'entrada',gf.alertOnExit&&'salida'].filter(Boolean).join(' + ')}</>:null}</div></Popup>
                    </Circle>
                  );
                } else {
                  const positions = gf.coordinates.map(c => [c.lat, c.lng]);
                  return (
                    <Polygon key={gf.id} positions={positions} pathOptions={{ color:gf.color, fillColor:gf.color, fillOpacity:0.08, weight:1.5, opacity:0.6 }}>
                      <Popup><div style={{fontFamily:'sans-serif',fontSize:13}}><b>{gf.name}</b><br/>Polígono — {gf.coordinates.length} vértices{gf.speedLimit?<><br/>Vel. máx: {gf.speedLimit} km/h</>:null}{gf.alertOnEnter||gf.alertOnExit?<><br/>🔔 {[gf.alertOnEnter&&'entrada',gf.alertOnExit&&'salida'].filter(Boolean).join(' + ')}</>:null}</div></Popup>
                    </Polygon>
                  );
                }
              })}

              {/* New circle preview */}
              {drawMode && drawType==='circle' && newGfCenter && (
                <Circle center={[newGfCenter.lat,newGfCenter.lng]} radius={parseFloat(newGfForm.radius)||200} pathOptions={{ color:newGfForm.color, fillColor:newGfForm.color, fillOpacity:0.1, weight:2, dashArray:'6 4' }}/>
              )}

              {/* Polygon drawing preview */}
              {drawMode && drawType==='polygon' && polyPoints.length > 0 && (
                <>
                  {polyPoints.length > 1 && <Polyline positions={polyPoints.map(p=>[p.lat,p.lng])} pathOptions={{ color:newGfForm.color, weight:2, dashArray:'6 4' }}/>}
                  {polyPoints.map((p,i) => <Marker key={i} position={[p.lat,p.lng]} icon={createPolyVertexIcon(i)}/>)}
                  {polyPoints.length >= 3 && (
                    <Polygon positions={polyPoints.map(p=>[p.lat,p.lng])} pathOptions={{ color:newGfForm.color, fillColor:newGfForm.color, fillOpacity:0.1, weight:2, dashArray:'6 4' }}/>
                  )}
                </>
              )}

              {/* History overlay */}
              {!replayMode && historyPoints.length > 1 && (
                <>
                  <Polyline positions={historyPoints.map(p=>[p.lat,p.lng])} pathOptions={{ color:'#FF9500', weight:3, opacity:0.85 }}/>
                  <Marker position={[historyPoints[0].lat,historyPoints[0].lng]} icon={createWaypointIcon('🏁')}/>
                  <Marker position={[historyPoints[historyPoints.length-1].lat,historyPoints[historyPoints.length-1].lng]} icon={createWaypointIcon('🔴')}/>
                </>
              )}
              {replayMode && historyPoints.length > 1 && <RouteReplay points={historyPoints}/>}

              {/* Convoy lines */}
              {activePanel==='convoy' && convoyUsers.length>1 && <Polyline positions={convoyUsers.map(u=>[u.lat,u.lng])} pathOptions={{ color:'#AF52DE', weight:2, opacity:0.55, dashArray:'8 6' }}/>}

              {/* Dispatch pin */}
              {dispatchPin && <Marker position={[dispatchPin.lat,dispatchPin.lng]} icon={createDispatchPinIcon()}/>}

              {/* User markers */}
              {locatedUsers.map(user => {
                const isPanic = activePanics.some(a=>a.userId===user.id);
                const isMoving = user.speed!=null && user.speed>0.5;
                const stopped = isStopped(user);
                const icon = createUserIcon(user.avatarColor||'#00D4FF', false, isPanic, user.heading, isMoving, stopped, user.username, user.mapEmoji);
                const stats = todayStats[user.id];
                const sp = user.speed!=null?(user.speed*3.6).toFixed(1):null;
                return (
                  <React.Fragment key={user.id}>
                    {user.accuracy && user.accuracy<500 && <Circle center={[user.lat,user.lng]} radius={user.accuracy} pathOptions={{ color:user.avatarColor||'#00D4FF', fillColor:user.avatarColor||'#00D4FF', fillOpacity:0.05, weight:1, opacity:0.3 }}/>}
                    <Marker position={[user.lat,user.lng]} icon={icon} eventHandlers={{ click:()=>{ setTelemetryUserId(user.id); if(isAdmin) fetchTodayStats(user.id); } }}>
                      <Popup className="rt-popup" minWidth={220}>
                        <div style={{ fontFamily:'var(--rt-font-sans)', background:'#fff', color:'#1D1D1F', borderRadius:12, padding:4 }}>
                          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
                            <div style={{
                              width:36,height:36,borderRadius:'50%',
                              background:`linear-gradient(135deg, ${user.avatarColor||'#007AFF'}, #AF52DE)`,
                              display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontWeight:700,fontSize:14,
                              boxShadow:'0 4px 10px rgba(0,0,0,.10)',
                            }}>{(user.username?.[0]||'?').toUpperCase()}</div>
                            <div>
                              <div style={{ fontWeight:700, fontSize:14, color:'#1D1D1F', letterSpacing:'-0.2px' }}>{user.username}</div>
                              <div style={{ fontSize:11, color:user.isOnline?'#34C759':'#86868B', fontWeight:600 }}>{user.isOnline?'● Online':'○ Offline'}</div>
                            </div>
                          </div>
                          {isPanic && <div style={{ background:'#FFE5EC', border:'1px solid rgba(255,45,85,0.20)', borderRadius:8, padding:'5px 10px', marginBottom:8, color:'#FF2D55', fontSize:12, fontWeight:700 }}>🚨 ALERTA DE PÁNICO ACTIVA</div>}
                          {user.mapStatus && (
                            <div style={{ background:'#E6F1FF', border:'1px solid rgba(0,122,255,0.18)', borderRadius:8, padding:'4px 10px', marginBottom:6, fontSize:11, color:'#007AFF', fontWeight:600 }}>
                              {user.mapStatus==='disponible'?'✅ Disponible':user.mapStatus==='ocupado'?'🔴 Ocupado':user.mapStatus==='en_transito'?'🚗 En tránsito':user.mapStatus==='descanso'?'⏸ En descanso':user.mapStatus==='emergencia'?'🚨 Emergencia':user.mapStatus}
                            </div>
                          )}
                          {stopped && <div style={{ background:'#FFF3DE', border:'1px solid rgba(255,159,10,0.30)', borderRadius:8, padding:'5px 10px', marginBottom:6, color:'#FF9500', fontSize:11, fontWeight:600 }}>⚠️ Sin movimiento por más de 3 min</div>}
                          <div style={{ fontSize:11, color:'#48484A', marginBottom:3, fontFamily:'var(--rt-font-mono)' }}><MapPin size={10} style={{ display:'inline', marginRight:4 }}/>{user.lat?.toFixed(5)}, {user.lng?.toFixed(5)}</div>
                          <div style={{ fontSize:12, marginBottom:3 }}>
                            {isMoving?<span style={{ color:'#34C759', fontWeight:700, fontFamily:'var(--rt-font-mono)' }}>🚀 {sp} km/h</span>:<span style={{ color:'#86868B', fontWeight:600 }}>🔵 Estático</span>}
                            {user.heading!=null&&isMoving&&<span style={{ color:'#48484A', fontSize:11, marginLeft:8, fontFamily:'var(--rt-font-mono)' }}>🧭 {headingLabel(user.heading)} ({Math.round(user.heading)}°)</span>}
                          </div>
                          {user.accuracy&&<div style={{ fontSize:11, color:'#86868B', marginBottom:3 }}>📡 ±{Math.round(user.accuracy)}m</div>}
                          <div style={{ fontSize:11, color:'#86868B', marginBottom:6 }}>Ubicación: {timeAgo(user.lastLocationAt)}</div>
                          {stats&&<div style={{ borderTop:'1px solid rgba(0,0,0,0.06)', paddingTop:6, marginBottom:6 }}><div style={{ fontSize:10, color:'#86868B', textTransform:'uppercase', marginBottom:2, fontWeight:700, letterSpacing:'0.4px' }}>Hoy</div><div style={{ fontSize:11, color:'#48484A', fontWeight:500 }}>📏 {stats.totalDistanceKm} km · ⚡ Máx {stats.maxSpeedKmh} km/h</div></div>}
                          {user.deviceInfo&&<div style={{ fontSize:11, color:'#86868B', borderTop:'1px solid rgba(0,0,0,0.06)', paddingTop:6, marginBottom:8 }}>{parseOS(user.deviceInfo.userAgent, user.deviceInfo.platform)} · {parseUA(user.deviceInfo.userAgent)}</div>}
                          {isAdmin&&<div style={{ borderTop:'1px solid rgba(0,0,0,0.06)', paddingTop:8, display:'flex', flexDirection:'column', gap:5 }}>
                            <button onClick={()=>{ const t=new Date().toISOString().slice(0,10); setHistoryUserId(user.id); setHistoryDate(t); setActivePanel('history'); loadHistoryForUser(user.id,t); }} style={popupBtn('#007AFF')}>📍 Ver ruta de hoy</button>
                            <button onClick={()=>{ setHistoryUserId(user.id); setActivePanel('history'); }} style={popupBtn('#86868B')}>📅 Historial por fecha</button>
                            <button onClick={()=>exportGPX(user.id)} style={popupBtn('#FF9500')}>⬇️ Exportar GPX hoy</button>
                          </div>}
                        </div>
                      </Popup>
                    </Marker>
                  </React.Fragment>
                );
              })}

              {activePanics.filter(a=>a.lat&&a.lng&&!locatedUsers.find(u=>u.id===a.userId)).map(a=>(
                <Marker key={`panic-${a.id}`} position={[a.lat,a.lng]} icon={createUserIcon('#FF2D55',false,true,null,false,false,'!','🚨')}>
                  <Popup><div style={{fontFamily:'var(--rt-font-sans)'}}><div style={{color:'#FF2D55',fontWeight:800,letterSpacing:'-0.2px'}}>🚨 ALERTA</div><div style={{fontSize:12,color:'#1D1D1F'}}>{a.username||a.userId}</div></div></Popup>
                </Marker>
              ))}

          {/* Floating PTT button */}
          <div style={{ position:'absolute', bottom:24, right:24, zIndex:1000, display:'flex', flexDirection:'column', alignItems:'center', gap:8 }}>
            {/* Channel indicator */}
            {currentChannel && (
              <div style={{
                background:'rgba(255,255,255,0.85)', WebkitBackdropFilter:'saturate(180%) blur(20px)', backdropFilter:'saturate(180%) blur(20px)',
                border:'1px solid rgba(255,255,255,0.7)', borderRadius:999, padding:'5px 14px', fontSize:11, color:'#007AFF',
                fontWeight:700, display:'flex', alignItems:'center', gap:5, maxWidth:160, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
                boxShadow:'0 4px 12px rgba(0,0,0,0.06)',
              }}>
                <Radio size={10}/> {currentChannel.name || 'Canal'}
              </div>
            )}
            {/* Transmitting user indicator */}
            {isTransmitting && transmittingUser && transmittingUser.userId !== currentUser?.id && (
              <div style={{ background:'#FFE5EC', border:'1px solid rgba(255,45,85,0.20)', borderRadius:999, padding:'5px 14px', fontSize:11, color:'#FF2D55', fontWeight:700, display:'flex', alignItems:'center', gap:5 }}>
                📻 {transmittingUser.username}…
              </div>
            )}
            {/* PTT button */}
            <button
              onMouseDown={handlePttDown} onMouseUp={handlePttUp}
              onTouchStart={(e)=>{ e.preventDefault(); handlePttDown(); }} onTouchEnd={(e)=>{ e.preventDefault(); handlePttUp(); }}
              disabled={!currentChannel || (isTransmitting && transmittingUser?.userId !== currentUser?.id)}
              style={{
                width:72, height:72, borderRadius:'50%', border:'none', cursor: currentChannel ? 'pointer' : 'not-allowed',
                background: pttPressed
                  ? 'radial-gradient(circle at 35% 35%, #FF2D55, #FF3B30)'
                  : isTransmitting && transmittingUser?.userId === currentUser?.id
                  ? 'radial-gradient(circle at 35% 35%, #FF375F, #FF2D55)'
                  : currentChannel
                  ? 'radial-gradient(circle at 35% 35%, #007AFF, #5856D6)'
                  : 'radial-gradient(circle at 35% 35%, #D2D2D7, #AEAEB2)',
                boxShadow: pttPressed
                  ? '0 0 36px rgba(255,45,85,0.50), 0 12px 24px rgba(255,45,85,0.30)'
                  : currentChannel
                  ? '0 0 28px rgba(0,122,255,0.35), 0 12px 24px rgba(0,122,255,0.25)'
                  : '0 2px 8px rgba(0,0,0,0.10)',
                display:'flex', alignItems:'center', justifyContent:'center',
                transition:'all 0.1s',
                transform: pttPressed ? 'scale(0.95)' : 'scale(1)',
                userSelect:'none', WebkitUserSelect:'none',
                color:'#fff',
              }}
              title={currentChannel ? 'Mantén pulsado para transmitir' : 'Únete a un canal primero'}
            >
              {pttPressed || (isTransmitting && transmittingUser?.userId === currentUser?.id)
                ? <Mic size={28} color="#fff"/>
                : currentChannel
                ? <Mic size={26} color="#fff"/>
                : <MicOff size={24} color="#fff"/>
              }
            </button>
            {!currentChannel && (
              <div style={{ fontSize:10, color:'#86868B', textAlign:'center', maxWidth:80, fontWeight:600 }}>Sin canal activo</div>
            )}
          </div>

            </MapContainer>
          )}
        </div>

        {/* Sidebar */}
        <aside style={{
          width:activePanel?300:260,
          background:'rgba(255,255,255,0.78)',
          WebkitBackdropFilter:'saturate(180%) blur(30px)',
          backdropFilter:'saturate(180%) blur(30px)',
          borderLeft:'1px solid rgba(0,0,0,0.06)',
          display:'flex', flexDirection:'column', overflow:'hidden', flexShrink:0, transition:'width 0.2s',
        }}>
          {renderPanel()}

          <div style={{ padding:'12px 12px 8px', borderBottom:'1px solid rgba(0,0,0,0.06)', flexShrink:0 }}>
            <div style={statLabel}>Filtros</div>
            <div style={{ display:'flex', gap:4 }}>
              {['all','online','located'].map(f=>(
                <button key={f} onClick={()=>setFilter(f)} style={{
                  flex:1, padding:'5px 0', borderRadius:8, border:'none',
                  background: filter===f ? '#1D1D1F' : '#F5F5F7',
                  color: filter===f ? '#fff' : '#48484A',
                  fontSize:10, cursor:'pointer', fontWeight:700, letterSpacing:'0.2px',
                }}>
                  {f==='all'?'Todos':f==='online'?'Online':'En campo'}
                </button>
              ))}
            </div>
          </div>

          {isAdmin && activePanics.length > 0 && (
            <div style={{ padding:'10px 12px', borderBottom:'1px solid rgba(255,45,85,0.18)', background:'#FFE5EC', flexShrink:0 }}>
              <div style={{ fontSize:10, color:'#FF2D55', textTransform:'uppercase', fontWeight:800, marginBottom:6, letterSpacing:'0.4px' }}>🚨 Alertas ({activePanics.length})</div>
              {activePanics.slice(0,3).map(a=>(
                <div key={a.id} style={{ padding:'7px 10px', marginBottom:4, background:'#fff', borderRadius:8, border:'1px solid rgba(255,45,85,0.20)' }}>
                  <div style={{ fontSize:12, color:'#FF2D55', fontWeight:700 }}>{a.username||'Usuario'}</div>
                  <div style={{ fontSize:10, color:'#86868B', fontFamily:'var(--rt-font-mono)' }}>{timeAgo(a.createdAt)}</div>
                  <button onClick={()=>resolveAlert(a.id)} style={{ marginTop:4, padding:'3px 10px', borderRadius:6, border:'none', background:'#34C759', color:'#fff', fontSize:10, cursor:'pointer', fontWeight:700 }}>✓ Resolver</button>
                </div>
              ))}
            </div>
          )}

          {gfEventFeed.length > 0 && (
            <div style={{ padding:'8px 12px', borderBottom:'1px solid rgba(0,0,0,0.06)', flexShrink:0 }}>
              <div style={{ fontSize:10, color:'#86868B', marginBottom:2, fontWeight:700, letterSpacing:'0.4px' }}>GEOCERCAS</div>
              {gfEventFeed.slice(0,3).map((e,i)=>(
                <div key={i} style={{ fontSize:11, color:e.type==='enter'?'#34C759':'#FF3B30', padding:'2px 0', fontWeight:600 }}>
                  {e.type==='enter'?'→':'←'} {e.username} / {e.geofenceName}
                </div>
              ))}
            </div>
          )}

          <div className="scrollbar-thin" style={{ flex:1, overflowY:'auto', padding:'8px 0' }}>
            <div style={{ padding:'0 12px 6px', fontSize:10, color:'#86868B', textTransform:'uppercase', letterSpacing:'0.4px', fontWeight:700 }}>Usuarios ({filteredUsers.length})</div>
            {filteredUsers.map(user => {
              const hasLoc=!!(user.lat&&user.lng);
              const isPanic=activePanics.some(a=>a.userId===user.id);
              const isMoving=user.speed!=null&&user.speed>0.5;
              const sp=user.speed!=null?(user.speed*3.6).toFixed(0):null;
              return (
                <div key={user.id} onClick={()=>hasLoc&&setFlyTo({lat:user.lat,lng:user.lng})} style={{ padding:'8px 12px', display:'flex', alignItems:'center', gap:10, borderBottom:'1px solid rgba(0,0,0,0.04)', background:isPanic?'#FFE5EC':'transparent', cursor:hasLoc?'pointer':'default' }}>
                  <div style={{
                    width:30,height:30,borderRadius:'50%',
                    background:`linear-gradient(135deg, ${user.avatarColor||'#007AFF'}, #AF52DE)`,
                    display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontWeight:700,fontSize:12,flexShrink:0,
                    border:isPanic?'2px solid #FF2D55':'none',
                    boxShadow: isPanic ? '0 0 10px rgba(255,45,85,0.5)' : '0 1px 2px rgba(0,0,0,0.06)',
                  }}>{(user.username?.[0]||'?').toUpperCase()}</div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:13, fontWeight:600, color:isPanic?'#FF2D55':'#1D1D1F', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', letterSpacing:'-0.1px' }}>{user.username}{isPanic?' 🚨':''}</div>
                    <div style={{ fontSize:10, color:'#86868B', display:'flex', alignItems:'center', gap:4 }}>
                      {user.isOnline?<><span style={{color:'#34C759'}}>●</span> Online{isMoving?<span style={{color:'#34C759', fontFamily:'var(--rt-font-mono)', fontWeight:600}}> · {sp} km/h</span>:''}</>:<><span style={{color:'#AEAEB2'}}>○</span> {timeAgo(user.lastSeen)}</>}
                      {user.mapStatus && <span style={{marginLeft:2}}>{user.mapStatus==='disponible'?'✅':user.mapStatus==='ocupado'?'🔴':user.mapStatus==='en_transito'?'🚗':user.mapStatus==='descanso'?'⏸':user.mapStatus==='emergencia'?'🚨':''}</span>}
                    </div>
                  </div>
                  <MapPin size={12} color={hasLoc?'#007AFF':'#D2D2D7'}/>
                </div>
              );
            })}
            {!filteredUsers.length&&<div style={{ padding:'20px 12px', textAlign:'center', color:'#86868B', fontSize:12 }}>Sin usuarios</div>}
          </div>

          <div style={{ padding:'10px 12px', borderTop:'1px solid rgba(0,0,0,0.06)', fontSize:11, color:'#48484A', fontWeight:500 }}>
            <div>📍 {locatedUsers.length} ubicados · 🟢 {users.filter(u=>u.isOnline).length} online</div>
            <div>🚀 {users.filter(u=>u.speed!=null&&u.speed>0.5).length} en mov · ⛔ {users.filter(u=>isStopped(u)).length} detenidos</div>
            <div style={{ marginTop:2 }}>🔔 {audioEnabled?'Alarmas ON':'Alarmas SILENCIADAS'}</div>
          </div>
        </aside>
      </div>
      <TelemetryPanel
        user={users.find(u => u.id === telemetryUserId)}
        trail={trailsRef.current[telemetryUserId] || []}
        onClose={() => setTelemetryUserId(null)}
      />
    </div>
  );
}

// ── Telemetry slide-in panel (Feature #8) ──────────────────────────────────────
function TelemetryPanel({ user, trail, onClose }) {
  if (!user) return null;
  const speedKmh = user.speed != null ? (user.speed * 3.6).toFixed(1) : null;
  const head = user.heading;
  const battery = user.deviceInfo?.battery;
  const accuracy = user.accuracy;
  const lastTs = user.lastLocationAt ? new Date(user.lastLocationAt) : null;
  const ageMin = lastTs ? Math.round((Date.now() - lastTs.getTime()) / 60000) : null;
  return (
    <div
      style={{
        position: 'fixed', top: 64, right: 12, bottom: 12, width: 320, zIndex: 1100,
        background: 'rgba(255,255,255,0.94)',
        WebkitBackdropFilter: 'saturate(180%) blur(30px)',
        backdropFilter: 'saturate(180%) blur(30px)',
        border: '1px solid rgba(255,255,255,0.7)',
        borderRadius: 14,
        boxShadow: '0 20px 50px rgba(0,0,0,.14)',
        animation: 'slideInRight 0.25s ease-out',
        display: 'flex', flexDirection: 'column',
        padding: 14, gap: 10,
      }}
    >
      <style>{'@keyframes slideInRight{from{transform:translateX(20px);opacity:0}to{transform:translateX(0);opacity:1}}'}</style>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{
          width: 38, height: 38, borderRadius: '50%',
          background: `linear-gradient(135deg, ${user.avatarColor || '#007AFF'}, #AF52DE)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', fontWeight: 800, fontSize: 14, boxShadow: '0 4px 10px rgba(0,0,0,.10)',
        }}>{(user.username?.[0] || '?').toUpperCase()}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: '#1D1D1F', letterSpacing: '-0.2px' }}>{user.username}</div>
          <div style={{ fontSize: 11, color: user.isOnline ? '#34C759' : '#86868B', fontWeight: 600 }}>
            {user.isOnline ? '● Online' : '○ Offline'}
          </div>
        </div>
        <button onClick={onClose}
          style={{ background: '#F5F5F7', border: 'none', borderRadius: 8, padding: '5px 10px', cursor: 'pointer', color: '#48484A' }}>
          ✕
        </button>
      </div>

      {/* Telemetry grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <TelemetryCell label="Velocidad" value={speedKmh != null ? speedKmh : '—'} unit={speedKmh != null ? 'km/h' : ''} hue="#34C759" />
        <TelemetryCell label="Heading" value={head != null ? Math.round(head) + '°' : '—'} unit={head != null ? headingLabel(head) : ''} hue="#007AFF" />
        <TelemetryCell label="Batería" value={battery != null ? battery + '%' : '—'} hue="#FF9500" />
        <TelemetryCell label="Precisión GPS" value={accuracy != null ? Math.round(accuracy) : '—'} unit={accuracy != null ? 'm' : ''} hue="#5856D6" />
      </div>

      {/* Position */}
      <div style={{ background: '#F5F5F7', borderRadius: 10, padding: '8px 10px' }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: '#86868B', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Posición</div>
        <div style={{ fontSize: 12, fontFamily: 'var(--rt-font-mono)', color: '#1D1D1F', marginTop: 2 }}>
          {user.lat != null && user.lng != null ? `${user.lat.toFixed(5)}, ${user.lng.toFixed(5)}` : 'sin GPS'}
        </div>
      </div>

      {/* Last update */}
      <div style={{ background: '#F5F5F7', borderRadius: 10, padding: '8px 10px' }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: '#86868B', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Última actualización</div>
        <div style={{ fontSize: 12, color: '#1D1D1F', marginTop: 2 }}>
          {lastTs ? `${lastTs.toLocaleTimeString('es-MX')}${ageMin != null ? ' · hace ' + ageMin + ' min' : ''}` : '—'}
        </div>
      </div>

      {/* Trail summary */}
      <div style={{ background: '#E6F1FF', borderRadius: 10, padding: '8px 10px' }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: '#007AFF', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Estela</div>
        <div style={{ fontSize: 12, color: '#1D1D1F', marginTop: 2 }}>
          {trail.length} puntos · últimos 4 min
        </div>
      </div>
    </div>
  );
}

function TelemetryCell({ label, value, unit, hue }) {
  return (
    <div style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.05)', borderRadius: 10, padding: '8px 10px' }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: '#86868B', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginTop: 2 }}>
        <span style={{ fontSize: 18, fontWeight: 800, color: hue }}>{value}</span>
        {unit && <span style={{ fontSize: 11, color: '#86868B' }}>{unit}</span>}
      </div>
    </div>
  );
}

const headerBtn = { background:'rgba(255,255,255,0.62)', WebkitBackdropFilter:'saturate(180%) blur(20px)', backdropFilter:'saturate(180%) blur(20px)', border:'1px solid rgba(0,0,0,0.06)', borderRadius:10, padding:'7px 11px', color:'#1D1D1F', cursor:'pointer', display:'flex', alignItems:'center', gap:6, fontSize:13, fontWeight:600 };
const panelStyle = { padding:'12px', borderBottom:'1px solid rgba(0,0,0,0.06)', background:'rgba(255,255,255,0.62)', flexShrink:0, maxHeight:'60vh', overflowY:'auto' };
const inputStyle = { width:'100%', padding:'7px 10px', borderRadius:8, background:'#fff', border:'1px solid rgba(0,0,0,0.10)', color:'#1D1D1F', fontSize:12, boxSizing:'border-box', boxShadow:'inset 0 1px 2px rgba(0,0,0,.03)' };
const primaryBtn = { flex:1, padding:'8px 0', borderRadius:8, background:'linear-gradient(135deg,#007AFF,#5856D6)', border:'none', color:'#fff', cursor:'pointer', fontSize:12, fontWeight:700, boxShadow:'0 4px 10px rgba(0,122,255,.30)' };
const disabledBtn = { flex:1, padding:'8px 0', borderRadius:8, background:'#F5F5F7', border:'none', color:'#AEAEB2', cursor:'not-allowed', fontSize:12, fontWeight:600 };
const secondaryBtn = { padding:'7px 12px', borderRadius:8, background:'#F5F5F7', border:'none', color:'#48484A', cursor:'pointer', fontSize:12, fontWeight:600 };
const statBox = { marginTop:8, padding:10, background:'#FFF3DE', borderRadius:8, border:'1px solid rgba(255,159,10,0.20)' };
const statLabel = { fontSize:10, color:'#86868B', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:4, fontWeight:700 };
const statRow = { fontSize:11, color:'#48484A', padding:'1px 0' };
const checkLabel = { fontSize:11, color:'#48484A', display:'flex', alignItems:'center', gap:5, cursor:'pointer' };
const popupBtn = (color) => ({ padding:'7px 11px', borderRadius:8, background:`${color}1a`, border:'none', color, cursor:'pointer', fontSize:12, textAlign:'left', width:'100%', fontWeight:600 });
const instructionBox = (color) => ({ fontSize:11, color, padding:'7px 10px', background:`${color}14`, borderRadius:8, marginBottom:6, border:`1px solid ${color}33`, fontWeight:600 });

function PanelHeader({ title, onClose }) {
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
      <div style={{ fontSize:11, color:'#86868B', fontWeight:800, letterSpacing:'0.4px', textTransform:'uppercase' }}>{title}</div>
      <button onClick={onClose} style={{ background:'none', border:'none', color:'#86868B', cursor:'pointer', padding:2 }}><X size={14}/></button>
    </div>
  );
}
